<script setup>
import HeartEmpty from '~/assets/images/svg/Heart_Empty.svg'
import HeartFull from '~/assets/images/svg/Heart_Filled.svg'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
})

// Access stores and auth state
const { $auth } = useNuxtApp()
const isLoggedIn = computed(() => $auth.loggedIn)
const userListStore = useUserList()
const layout = useLayout()

// Local state for modal visibility and login flow
const isAddToListModalOpen = ref(false)
const awaitingLoginForAddToList = ref(false)
const isModalLoading = ref(false)

// Function to handle when lists are saved in the modal
const onListSaved = (changes) => {
  userListStore.showToast(changes)
}

// Computed property to track the loading state
const isLoading = computed(() => {
  if (!isLoggedIn.value || !props.product?.product_id) {
    return false
  }
  // The status is loading if it's undefined in the store
  const statusLoading =
    userListStore.getProductStatus(props.product.product_id) === undefined

  return statusLoading || isModalLoading.value
})

onMounted(() => {
  // We only fetch statuses if the user is logged in
  if (props.product?.product_id && isLoggedIn.value) {
    userListStore.queueProductStatus(props.product.product_id)
  }
})

// Determines if the heart icon should be filled or empty
const isHeartActive = computed(() => {
  if (!isLoggedIn.value || !props.product?.product_id) {
    return false
  }
  const status = userListStore.getProductStatus(props.product.product_id)

  if (!status) return false // Default to false while loading

  // Heart is full if in ANY list OR if notify_me is true
  return status.lists.length > 0 || status.notify_me
})

// Dynamically selects the correct SVG source based on the icon's state.
const iconSrc = computed(() => (isHeartActive.value ? HeartFull : HeartEmpty))

const altText = computed(() =>
  isHeartActive.value
    ? 'Product is in your lists or notify me'
    : 'Add product to your lists or notify me',
)

// Handles the click event on the heart icon
async function handleHeartClick() {
  if (!isLoggedIn.value) {
    awaitingLoginForAddToList.value = true
    $auth.openAuthModal()
    $auth.postLoginActionPending = true
    return
  }

  isModalLoading.value = true
  layout.isLoading = true
  try {
    await userListStore.loadAllLists()
    await userListStore.fetchProductStatusImmediately(props.product.product_id)
    isAddToListModalOpen.value = true
  } catch (error) {
    console.error('Failed to load modal data:', error)
  } finally {
    isModalLoading.value = false
    layout.isLoading = false
  }
}

// Watch for login state changes to handle modal opening after login
watch(
  () => ({ loggedIn: isLoggedIn.value, modalOpen: $auth.authModalOpen }),
  (newState, oldState) => {
    const justLoggedIn = newState.loggedIn && !oldState.loggedIn
    const modalJustClosed = !newState.modalOpen && oldState.modalOpen

    if (justLoggedIn) {
      userListStore.queueProductStatus(props.product.product_id)
    }

    // --- Success Scenario ---
    // The user just logged in, and we were waiting to open the modal.
    if (justLoggedIn && awaitingLoginForAddToList.value) {
      awaitingLoginForAddToList.value = false
      $auth.postLoginActionPending = false
      handleHeartClick()
      return
    }

    // --- Cancellation Scenario ---
    // The modal closed, we were waiting, but the user is still not logged in.
    if (
      modalJustClosed &&
      awaitingLoginForAddToList.value &&
      !newState.loggedIn
    ) {
      awaitingLoginForAddToList.value = false
      $auth.postLoginActionPending = false
    }
  },
  { deep: true },
)
</script>

<template>
  <div class="heart-icon-container">
    <div class="heart-button-container">
      <button
        class="heart-button"
        :class="{ 'is-loading': isLoading }"
        :aria-label="altText"
        :disabled="isLoading"
        @click="handleHeartClick"
      >
        <img :src="iconSrc" :alt="altText" />
      </button>
    </div>

    <Teleport to="body">
      <UserListModal
        :visible="isAddToListModalOpen"
        :product="product"
        @close="isAddToListModalOpen = false"
        @saved="onListSaved"
      />
    </Teleport>
  </div>
</template>
